import { get, post, patch, del } from "../common/apiClient";
import { toAbsoluteUploadUrl } from "./UploadService";

/**
 * Danh sách conversation (không kèm messages).
 */
export function fetchChatList({ page = 1, limit = 100, projectId } = {}) {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("limit", String(limit));
  if (projectId && projectId !== "all") {
    params.append("projectId", String(projectId));
  }
  return get(`/chat?${params.toString()}`);
}

export function fetchChatById(id) {
  return get(`/chat/${encodeURIComponent(id)}`);
}

export function createChat({ title = "Chat mới", model = "", projectId = null } = {}) {
  return post("/chat", { title, model, projectId });
}

/**
 * Lưu các message vào lịch sử sau khi đã stream xong từ provider ngoài
 * (vd: /anthropic/v1/messages). Trả về { data: { conversationId } }.
 */
export function saveChatMessages({
  conversationId = null,
  title = "Chat mới",
  model = "",
  projectId = null,
  messages = [],
} = {}) {
  return post("/chat/messages", {
    conversationId,
    title,
    model,
    projectId,
    messages,
  });
}

export function renameChatById(id, title) {
  return patch(`/chat/${encodeURIComponent(id)}/title`, { title });
}

export function deleteAllChats() {
  return del("/chat");
}

export function deleteChatById(id) {
  return del(`/chat/${encodeURIComponent(id)}`);
}

// ── Pin / Project / Share ──────────────────────────────────────────────────

export function setPinned(id, pinned) {
  return patch(`/chat/${encodeURIComponent(id)}/pin`, { pinned });
}

export function assignProject(conversationId, projectId) {
  return patch(`/chat/${encodeURIComponent(conversationId)}/project`, {
    projectId: projectId || null,
  });
}

export function enableShare(id) {
  return post(`/chat/${encodeURIComponent(id)}/share`);
}

export function disableShare(id) {
  return del(`/chat/${encodeURIComponent(id)}/share`);
}

// ── Search ─────────────────────────────────────────────────────────────────

export function searchConversations({ q = "", page = 1, limit = 20 } = {}) {
  const params = new URLSearchParams();
  params.append("q", q);
  params.append("page", String(page));
  params.append("limit", String(limit));
  return get(`/chat/search?${params.toString()}`);
}

// ── Projects CRUD ──────────────────────────────────────────────────────────

export function listProjects() {
  return get("/chat/projects");
}

export function getProject(projectId) {
  return get(`/chat/projects/${encodeURIComponent(projectId)}`);
}

export function createProject({ name, prompt, color } = {}) {
  return post("/chat/projects", { name, prompt, color });
}

export function updateProject(id, { name, prompt, color } = {}) {
  return patch(`/chat/projects/${encodeURIComponent(id)}`, {
    name,
    prompt,
    color,
  });
}

export function deleteProject(id) {
  return del(`/chat/projects/${encodeURIComponent(id)}`);
}

// ── Project members (share / phân quyền) ─────────────────────────────────────

export function shareProject(projectId, { email, userId, role } = {}) {
  return post(`/chat/projects/${encodeURIComponent(projectId)}/members`, {
    email,
    userId,
    role,
  });
}

export function updateProjectMember(projectId, memberId, { role } = {}) {
  return patch(
    `/chat/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(memberId)}`,
    { role },
  );
}

export function removeProjectMember(projectId, memberId) {
  return del(
    `/chat/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(memberId)}`,
  );
}

// ── Messages ops ───────────────────────────────────────────────────────────

export function editMessageContent(conversationId, messageId, content) {
  return patch(
    `/chat/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}`,
    { content },
  );
}

export function deleteFromMessage(conversationId, messageId, { inclusive = true } = {}) {
  return del(
    `/chat/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/and-after?inclusive=${inclusive}`,
  );
}

export function setMessageFeedback(conversationId, messageId, feedback) {
  return patch(
    `/chat/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/feedback`,
    { feedback },
  );
}

// ── Branch ─────────────────────────────────────────────────────────────────

export function branchConversation(conversationId, { untilMessageId, title } = {}) {
  return post(`/chat/${encodeURIComponent(conversationId)}/branch`, {
    untilMessageId,
    title,
  });
}

// ── Utils ──────────────────────────────────────────────────────────────────

export function isObjectId(str) {
  return /^[a-f\d]{24}$/i.test(str);
}

export function mapServerRowToConversation(row) {
  return {
    id: row._id,
    title: row.title || "Chat mới",
    createdAt: row.updatedAt || row.createdAt || new Date().toISOString(),
    pinned: !!row.pinned,
    pinnedAt: row.pinnedAt || null,
    projectId: row.projectId || null,
    shared: !!row.shared,
    shareId: row.shareId || null,
    isDraft: false,
  };
}

function stripAttachmentMarkers(content) {
  return (content || "")
    .replace(/\[Hình ảnh: [^\]]+\]\s*/g, "")
    .replace(/\[Tệp: [^\]]+\]\s*/g, "")
    .trim();
}

/**
 * Parse user message content stored in DB.
 */
function parseDbUserContent(content) {
  if (!content) {
    return { content: "", attachedDocs: [] };
  }

  const attachedDocs = [];
  const tepRe = /\[Tệp: ([^\]]+)\]/g;
  let tepMatch;
  while ((tepMatch = tepRe.exec(content)) !== null) {
    attachedDocs.push({ name: tepMatch[1] });
  }

  if (!content.includes("[Nội dung file ")) {
    return { content: stripAttachmentMarkers(content), attachedDocs };
  }

  const re = /\[Nội dung file ([^\]]+)\]:/g;
  let m;
  while ((m = re.exec(content)) !== null) {
    attachedDocs.push({ name: m[1] });
  }

  const firstIdx = content.indexOf("[Nội dung file ");
  const displayContent = stripAttachmentMarkers(content.slice(0, firstIdx));

  return { content: displayContent, attachedDocs };
}

/**
 * Map messages từ DB → UI. Giữ cả `_id` thật (dbId) để thao tác edit/regenerate/branch.
 */
export function mapDbMessagesToUi(messages = []) {
  return messages.map((m, idx) => {
    const base = {
      id: `m-${idx}-${m.createdAt ? new Date(m.createdAt).getTime() : idx}`,
      dbId: m._id ? String(m._id) : null,
      role: m.role,
      timestamp: m.createdAt
        ? new Date(m.createdAt).toISOString()
        : new Date().toISOString(),
      model: m.model || undefined,
      edited: !!m.edited,
      editedAt: m.editedAt || null,
      feedback: m.feedback || null,
    };

    if (m.role === "user") {
      const { content, attachedDocs } = parseDbUserContent(m.content);
      const images = Array.isArray(m.images)
        ? m.images
            .filter((u) => typeof u === "string" && u.trim())
            .map((u) => toAbsoluteUploadUrl(u))
        : [];
      // File đính kèm đã lưu (URL) → chip có link tải (chỉ để hiển thị).
      const dbFiles = Array.isArray(m.files)
        ? m.files
            .filter((f) => f && typeof f.url === "string" && f.url.trim())
            .map((f) => ({
              url: toAbsoluteUploadUrl(f.url),
              name: f.name || "file",
              mimeType: f.mimeType || "",
              size: Number(f.size) || 0,
            }))
        : [];
      const mergedDocs =
        dbFiles.length > 0
          ? dbFiles.map((f) => ({ name: f.name, url: f.url }))
          : attachedDocs;

      // apiContent chỉ là TEXT khi load lại từ DB — KHÔNG nhét URL ảnh/file vào
      // request (backend không tải được URL có auth → 401). Bytes ảnh/file đã
      // được gửi base64 ở lượt gốc; lượt sau provider giữ session vẫn còn ngữ cảnh.
      return {
        ...base,
        content,
        attachedDocs: mergedDocs,
        images,
        apiContent: m.content,
      };
    }

    return { ...base, content: m.content };
  });
}
